/**
 * One thumbnail tile inside a PhotoReqCard.
 *
 * Two interaction modes:
 *   - Normal (default): tap → open review dialog. Hover-only X to
 *     delete. Corner badge shows approval/rejection status.
 *   - Selection (selectionMode=true): tap toggles the selection.
 *     Corner shows a filled/empty checkbox. Delete button is hidden
 *     to prevent muscle-memory nukes during a bulk workflow.
 *
 * Approved photos stay non-deletable for non-admin/non-pm users — the
 * X is replaced with a lock indicator so the field crew understands
 * why.
 */
import { fileUrl } from "@/lib/api";
import RetryImg from "@/components/photos/RetryImg";
import { PHOTO_DRAG_TYPE } from "@/lib/photoDrop";
import { Check, X, ShieldCheck, ShieldX, CheckSquare, Square, Play, Film } from "lucide-react";

export default function PhotoThumb({
  photo,
  canReview,
  onOpen,
  onRemove,
  selectionMode = false,
  selected = false,
  onToggleSelect,
}) {
  const status = photo.review_status || "pending";
  const locked = status === "approved" && !canReview;

  // Colored ring around the whole thumbnail — much easier to spot in
  // a grid of 100+ thumbs than the small corner badge alone. Selection
  // mode's own blue ring overrides these.
  const statusRing = selectionMode && selected
    ? "ring-2 ring-blue-500"
    : status === "rejected"
      ? "ring-2 ring-red-500 ring-offset-1 ring-offset-slate-900"
      : status === "pending"
        ? "ring-2 ring-amber-400/70 ring-offset-1 ring-offset-slate-900"
        : "";

  const handleClick = () => {
    if (selectionMode) {
      onToggleSelect?.(photo.id);
    } else {
      onOpen?.();
    }
  };

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={handleClick}
        draggable={!selectionMode}
        onDragStart={(e) => {
          // Carry only the photo id — the browser must not attach the thumbnail as a file.
          e.dataTransfer.setData(PHOTO_DRAG_TYPE, photo.id);
          e.dataTransfer.effectAllowed = "move";
        }}
        aria-pressed={selectionMode ? (selected ? "true" : "false") : undefined}
        className={`block w-full focus:outline-none focus:ring-2 focus:ring-blue-500 rounded ${statusRing}`}
        data-testid={`photo-thumb-${photo.id}`}
        aria-label={selectionMode ? (selected ? "Deselect photo" : "Select photo") : "Open photo for review"}
      >
        {photo.is_video && !photo.poster_path ? (
          <span className="w-full h-12 rounded bg-slate-800 flex items-center justify-center">
            <Film className="w-4 h-4 text-slate-400" />
          </span>
        ) : (
          <RetryImg
            src={fileUrl(photo.id, { size: "thumb" })}
            testId={`photo-thumb-img-${photo.id}`}
            alt=""
            draggable={false}
            loading="lazy"
            decoding="async"
            className={`w-full h-12 object-cover rounded ${
              status === "rejected" ? "opacity-60 grayscale" : ""
            } ${selectionMode && !selected ? "opacity-70" : ""}`}
          />
        )}
        {photo.is_video && (
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid={`photo-video-badge-${photo.id}`}>
            <span className="bg-black/60 rounded-full p-1"><Play className="w-3 h-3 text-white fill-white" /></span>
          </span>
        )}
        {/* Corner status badge — takes over for the selection checkbox
            when NOT in selection mode. Selection mode gets its own
            checkbox in the same corner. */}
        {selectionMode ? (
          <span
            className={`absolute -top-1 -left-1 rounded-full p-0.5 shadow ${
              selected ? "bg-blue-600" : "bg-slate-800 border border-slate-600"
            }`}
            data-testid={`photo-select-badge-${photo.id}`}
          >
            {selected ? (
              <CheckSquare className="w-3 h-3 text-white" />
            ) : (
              <Square className="w-3 h-3 text-slate-400" />
            )}
          </span>
        ) : status === "approved" ? (
          <span
            className="absolute -top-1 -left-1 bg-emerald-600 rounded-full p-0.5 shadow shadow-emerald-900/50"
            title="Approved"
            data-testid={`photo-status-badge-${photo.id}`}
          >
            <ShieldCheck className="w-3 h-3 text-white" />
          </span>
        ) : status === "rejected" ? (
          <span
            className="absolute -top-1 -left-1 bg-red-600 rounded-full p-0.5 shadow shadow-red-900/50"
            title={photo.review_reason || "Needs retake"}
            data-testid={`photo-status-badge-${photo.id}`}
          >
            <ShieldX className="w-3 h-3 text-white" />
          </span>
        ) : null}
      </button>
      {/* Delete affordance is intentionally hidden during selection
          mode — the primary action bar at the top handles bulk
          operations, and we don't want a stray hover to nuke a photo
          mid-triage. */}
      {!selectionMode && !locked && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-1 -right-1 bg-red-600 rounded-full p-0.5 opacity-0 group-hover:opacity-100"
          aria-label="Remove photo"
          data-testid={`photo-remove-${photo.id}`}
        >
          <X className="w-3 h-3"/>
        </button>
      )}
      {!selectionMode && locked && (
        <span
          className="absolute -top-1 -right-1 bg-slate-700 rounded-full p-0.5"
          title="Approved — locked"
        >
          <Check className="w-3 h-3 text-emerald-400"/>
        </span>
      )}
    </div>
  );
}
